import React, { useState, useMemo } from 'react';
import { 
    Box, Card, Typography, Stack, Tabs, Tab, TextField, IconButton, 
    TableContainer, Table, TableHead, TableRow, TableCell, TableBody 
} from '@mui/material';

// Icons
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import SortIcon from '@mui/icons-material/Sort';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { formatMoney } from '../producer-dashboard/ProducerDashboardHelper';
import { formatDate } from '../payout-dashboard/PayoutDashboardHelper';

// Helpers

type TransactionsSectionProps = {
    dashboardData: any;
    selectedTab: string;
};

export const TransactionsSection = ({ dashboardData, selectedTab } : TransactionsSectionProps) => {

    const [txTypeTab, setTxTypeTab] = useState(0); 
    const [txSearch, setTxSearch] = useState('');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

    const isScGlobal = selectedTab === 'SC_GLOBAL';

    const processedTransactions = useMemo(() => {
        if (!dashboardData || !selectedTab) return [];
        let walletTxs: any = { transactions_out: [], transactions_in: [], transactions_p2_p: [] };

        if (isScGlobal) {
            walletTxs = dashboardData.sc_transactions || walletTxs;
        } else {
            walletTxs = dashboardData.escrow_transactions_by_operator?.[selectedTab] || walletTxs;
        }
        
        let list: any[] = [];
        if (txTypeTab === 0) list = [...(walletTxs.transactions_out || [])];
        else if (txTypeTab === 1) list = [...(walletTxs.transactions_in || [])];
        else list = [...(walletTxs.transactions_p2_p || [])];

        if (txSearch) {
            const q = txSearch.toLowerCase();
            list = list.filter(t => 
                t.id?.toString().includes(q) || 
                t.comment?.toLowerCase().includes(q) || 
                t.reference?.toLowerCase().includes(q)
            );
        }

        return list.sort((a, b) => 
            sortOrder === 'asc' ? (a.date || 0) - (b.date || 0) : (b.date || 0) - (a.date || 0)
        );
    }, [dashboardData, selectedTab, isScGlobal, txTypeTab, txSearch, sortOrder]);

    return (
        <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 4 }}>
            {/* HEADER DYNAMIQUE */}
            <Box sx={{ p: 2, borderBottom: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
                    {isScGlobal ? (
                        <>
                            <AccountBalanceWalletIcon color="primary" /> 
                            Recent Transactions - Global SC Wallet
                        </>
                    ) : (
                        <>
                            <AccountBalanceIcon sx={{ color: '#16a34a' }} /> 
                            Recent Transactions - Escrow Wallet ({selectedTab})
                        </>
                    )}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Showing the latest movements recorded in the last 7 days.
                </Typography>
            </Box>

            {/* TOOLBAR (Onglets & Filtres) */}
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" sx={{ p: 2, borderBottom: '1px solid #e2e8f0' }}>
                <Tabs 
                    value={txTypeTab} 
                    onChange={(_, v) => setTxTypeTab(v)} 
                    sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, textTransform: 'none', fontWeight: 600 } }}
                >
                    <Tab label="Money Out" icon={<ArrowUpwardIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
                    <Tab label="Money In" icon={<ArrowDownwardIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
                    <Tab label="Internal (P2P)" icon={<SwapHorizIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
                </Tabs>
                <Stack direction="row" spacing={1} mt={{ xs: 2, sm: 0 }}>
                    <TextField 
                        size="small" 
                        placeholder="Search ID or Ref..." 
                        value={txSearch} 
                        onChange={e => setTxSearch(e.target.value)} 
                        sx={{ width: 220, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} 
                    />
                    <IconButton 
                        onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')} 
                        sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}
                    >
                        <SortIcon sx={{ transform: sortOrder === 'desc' ? 'rotate(180deg)' : 'none' }} />
                    </IconButton>
                </Stack>
            </Stack>
            
            {/* TABLEAU */}
            {processedTransactions.length > 0 ? (
                <TableContainer sx={{ maxHeight: 500 }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow sx={{ "& th": { bgcolor: '#f8fafc', color: 'text.secondary', fontWeight: 600, borderBottom: '1px solid #e2e8f0' } }}>
                                <TableCell sx={{ pl: 4 }}>Date & ID</TableCell>
                                <TableCell>Accounts</TableCell>
                                <TableCell>Description</TableCell>
                                <TableCell align="right" sx={{ pr: 4 }}>Amount</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {processedTransactions.map((tx: any) => {
                                const amountVal = (tx.credit_amount || tx.debit_amount || 0) / 100;
                                const isOut = txTypeTab === 0 || (txTypeTab === 2 && tx.sender_account_id?.includes("SC"));
                                return (
                                    <TableRow key={tx.id} hover sx={{ '& td': { borderBottom: '1px solid #f1f5f9' } }}>
                                        <TableCell sx={{ pl: 4 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatDate(tx.date)}</Typography>
                                            <Typography variant="caption" color="text.disabled">#{tx.id}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption" display="block" color="text.secondary">
                                                From: <span style={{ fontWeight: 600, color: '#0f172a' }}>{tx.sender_account_id || '-'}</span>
                                            </Typography>
                                            <Typography variant="caption" display="block" color="text.secondary">
                                                To: <span style={{ fontWeight: 600, color: '#0f172a' }}>{tx.receiver_account_id || '-'}</span>
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ color: '#0f172a' }}>{tx.comment || '-'}</Typography>
                                            <Typography variant="caption" color="text.disabled">Ref: {tx.reference}</Typography>
                                        </TableCell>
                                        <TableCell align="right" sx={{ pr: 4 }}>
                                            <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                                                <Typography sx={{ fontWeight: 700, color: isOut ? '#ef4444' : '#10b981' }}>
                                                    {isOut ? '-' : '+'}{formatMoney(amountVal, dashboardData.currency)}
                                                </Typography>
                                                {isOut ? <TrendingDownIcon sx={{ fontSize: 16, color: '#ef4444' }} /> : <TrendingUpIcon sx={{ fontSize: 16, color: '#10b981' }} />}
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : (
                <Box sx={{ py: 10, textAlign: 'center' }}>
                    <Typography color="text.disabled" variant="body2">No transactions match your criteria.</Typography>
                </Box>
            )}
        </Card>
    );
};